import { Link } from "@tanstack/react-router";
import { Linkedin, Instagram, Youtube, MessageCircle, Phone, Mail, MapPin } from "lucide-react";
import { TikTokIcon } from "@/components/icons/TikTokIcon";
import { KENTRO } from "@/lib/constants";
import { SEGMENT_CASES } from "@/lib/segments-data";
import { trackIaNav } from "@/lib/analytics";
import { useT, useLocale } from "@/i18n/LocaleProvider";
import { withLocale } from "@/i18n/types";
import logoBranca from "@/assets/kentro-logo-branca.svg.asset.json";
import logoRoxa from "@/assets/kentro-logo-roxa.svg.asset.json";
import metaBusinessPartner from "@/assets/meta-business-partner.webp.asset.json";
import inovativaBrasil from "@/assets/inovativa-brasil.webp.asset.json";
import seloLGPD from "@/assets/selo-lgpd.webp.asset.json";
import abrtelecom from "@/assets/abrtelecom.webp.asset.json";
import softwareAdvice from "@/assets/software-advice.webp.asset.json";

export function SiteFooter() {
  const t = useT();
  const locale = useLocale();
  const lp = (p: string) => withLocale(p, locale);

  return (
    <footer className="border-t border-white/5 bg-background pt-16 pb-10">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-7">
          <div className="lg:col-span-2">
            <Link to={lp("/")} aria-label="Kentro Sistemas">
              <img
                src={logoBranca.url}
                alt="Kentro Sistemas"
                width={180}
                height={52}
                className="h-12 w-auto block [html.light_&]:hidden"
                loading="lazy"
                decoding="async"
              />
              <img
                src={logoRoxa.url}
                alt="Kentro Sistemas"
                width={180}
                height={52}
                className="h-12 w-auto hidden [html.light_&]:block"
                loading="lazy"
                decoding="async"
              />
            </Link>
            <p className="mt-4 max-w-sm text-sm text-muted-foreground">{t.footer.tagline}</p>
            <ul className="mt-6 space-y-2 text-sm text-muted-foreground">
              {KENTRO.phones.map((p) => {
                const href =
                  p.tel === "553832208706" || p.tel === "08005502020" || p.tel === "5511915827716"
                    ? KENTRO.whatsappComercialUrl
                    : p.whatsapp
                      ? KENTRO.whatsappUrl(t.forms.waDefault)
                      : `tel:+${p.tel.replace(/\D/g, "")}`;
                const Icon = p.whatsapp ? MessageCircle : Phone;
                const external = p.whatsapp ? { target: "_blank", rel: "noopener noreferrer" } : {};
                return (
                  <li key={p.tel} className="flex items-center gap-2">
                    <Icon className="size-4" />
                    <a href={href} {...external} className="hover:text-foreground">
                      <span className="text-foreground/80">{p.label}:</span> {p.display}
                    </a>
                  </li>
                );
              })}
              <li className="flex items-center gap-2">
                <Mail className="size-4" />
                <a href={`mailto:${KENTRO.email}`} className="hover:text-foreground">
                  {KENTRO.email}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="size-4 shrink-0 mt-0.5" />
                <span>{KENTRO.address}</span>
              </li>
            </ul>
          </div>

          <FooterCol
            title={t.footer.productCol}
            items={[
              { label: t.nav.ia, to: lp("/ia"), onClick: () => trackIaNav("footer") },
              { label: "Omnichannel", to: lp("/omnichannel") },
              { label: "Gestão de Atendimento", to: lp("/gestao-de-atendimento") },
              { label: t.nav.features, to: "/funcionalidades" },
              { label: t.nav.integrations, to: lp("/integracoes") },
              { label: t.nav.cases, to: lp("/cases") },
              { label: "FAQ - Perguntas frequentes", to: lp("/perguntas-frequentes") },
            ]}
          />
          <FooterCol
            title="Segmentos"
            items={SEGMENT_CASES.map((s) => ({
              label: s.shortName,
              to: lp(`/segmentos/${s.slug}`),
            }))}
          />
          <FooterCol
            title="Campanhas"
            items={[
              { label: "Google Ads", to: lp("/google") },
              { label: "ChatGPT", to: "/chatgpt" },
              { label: "Instagram Ads", to: lp("/instagram") },
              { label: "TikTok Ads", to: lp("/tiktok") },
              { label: "Clínicas", to: lp("/clinicas") },
              { label: "Magistral", to: lp("/magistral") },
              { label: "WDEC", to: lp("/wdec") },
            ]}
          />
          <FooterCol
            title={t.footer.companyCol}
            items={[
              { label: "Sobre a Kentro", to: lp("/sobre") },
              { label: "Equipe", to: lp("/equipe") },
              { label: "Trabalhe conosco", href: `${lp("/equipe")}#trabalhe-conosco` },
              { label: "Parceria", to: lp("/parceria") },
              { label: "Academy", href: KENTRO.links.academy, external: true },
              { label: t.nav.blog, to: lp("/blog") },
              { label: "Podcast", href: KENTRO.links.youtube, external: true },
            ]}
          />

          <FooterCol
            title={t.footer.legalCol}
            items={[
              { label: "Termos de uso", to: lp("/termos-de-uso") },
              { label: t.footer.privacy, to: lp("/privacidade") },
              { label: t.footer.privacyNotice, to: lp("/aviso-de-privacidade") },
              { label: "LGPD · Tratamento de dados", to: lp("/lgpd") },
              { label: "Política de cookies", to: lp("/politica-de-cookies") },

              { label: "Preferências de cookies", href: "#", onClick: (e) => { e.preventDefault(); void import("@/lib/cookie-consent").then((m) => m.openCookiePreferences()); } },
              { label: t.footer.ombudsman, to: lp("/ouvidoria") },
              { label: "Status", href: KENTRO.links.status, external: true },
              { label: "Sitemap", href: "/sitemap.xml", external: true },
              { label: t.nav.contact, to: lp("/contato") },
            ]}
          />
        </div>

        <div className="mt-12 border-t border-white/5 pt-6">
          <div className="flex flex-wrap items-center gap-4">
            <a
              href="https://www.facebook.com/business/partner-directory"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Meta Business Partner"
              className="inline-flex items-center rounded-lg border border-white/10 bg-white/[0.03] p-2 transition hover:border-primary-glow/40"
            >
              <img
                src={metaBusinessPartner.url}
                alt="Selo Meta Business Partner"
                width={480}
                height={275}
                loading="lazy"
                decoding="async"
                className="h-12 w-auto"
              />
            </a>
            <a
              href="https://www.inovativabrasil.com.br/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Inovativa Brasil — Destaque 2023.2"
              className="group inline-flex flex-col items-center rounded-lg border border-white/10 bg-white/[0.03] p-2 transition hover:border-primary-glow/40"
            >
              <img
                src={inovativaBrasil.url}
                alt="Selo Inovativa Brasil"
                width={240}
                height={240}
                loading="lazy"
                decoding="async"
                className="h-12 w-auto"
              />
              <span className="mt-1 text-[10px] text-muted-foreground group-hover:text-foreground">Destaque 2023.2</span>
            </a>
            <a
              href="https://www.privacidade.com.br/portal-de-privacidade?token=88047695397ac976534ac691a2b9eb2b"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Canal de Comunicação LGPD — Empresa Amiga da Privacidade"
              className="inline-flex items-center rounded-lg border border-white/10 bg-white/[0.03] p-2 transition hover:border-primary-glow/40"
            >
              <img
                src={seloLGPD.url}
                alt="Selo Canal de Comunicação LGPD e Empresa Amiga da Privacidade"
                width={400}
                height={80}
                loading="lazy"
                decoding="async"
                className="h-12 w-auto"
              />
            </a>
            <a
              href="https://www.abrtelecom.com.br/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="ABR Telecom"
              className="inline-flex items-center rounded-lg border border-white/10 bg-white p-2 transition hover:border-primary-glow/40"
            >
              <img
                src={abrtelecom.url}
                alt="Selo ABR Telecom"
                width={432}
                height={96}
                loading="lazy"
                decoding="async"
                className="h-12 w-auto"
              />
            </a>
            <a
              href="https://www.softwareadvice.com/crm/kentro-profile/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Software Advice — Avaliação 4,9 estrelas"
              className="inline-flex items-center rounded-lg border border-white/10 bg-white/[0.03] p-2 transition hover:border-primary-glow/40"
            >
              <img
                src={softwareAdvice.url}
                alt="Software Advice — 4,9 de 5 estrelas"
                width={237}
                height={96}
                loading="lazy"
                decoding="async"
                className="h-12 w-auto"
              />
            </a>
          </div>
        </div>

        <div className="mt-8 flex flex-col items-start justify-between gap-4 border-t border-white/5 pt-6 sm:flex-row sm:items-center">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} Kentro Sistemas. {t.footer.rights}
            <span className="mx-2">·</span>
            CNPJ: 43.406.507/0001-52
          </p>
          <div className="flex items-center gap-3">
            <Social href={KENTRO.links.linkedin} icon={<Linkedin className="size-4" />} label="LinkedIn" />
            <Social href={KENTRO.links.instagram} icon={<Instagram className="size-4" />} label="Instagram" />
            <Social href={KENTRO.links.youtube} icon={<Youtube className="size-4" />} label="YouTube" />
            <Social href={KENTRO.links.tiktok} icon={<TikTokIcon className="size-4" />} label="TikTok" />
            <Social href={KENTRO.whatsappComercialUrl} icon={<MessageCircle className="size-4" />} label="WhatsApp" />
          </div>
        </div>
      </div>
    </footer>
  );
}

type Item = { label: string; href?: string; to?: string; external?: boolean; onClick?: (e: React.MouseEvent) => void };

function FooterCol({ title, items }: { title: string; items: Item[] }) {
  return (
    <div>
      <h3 className="text-sm font-semibold">{title}</h3>
      <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
        {items.map((it) =>
          it.to ? (
            <li key={it.label}>
              <Link to={it.to} onClick={it.onClick} className="hover:text-foreground">
                {it.label}
              </Link>
            </li>
          ) : (
            <li key={it.label}>
              <a
                href={it.href}
                target={it.external ? "_blank" : undefined}
                rel={it.external ? "noopener noreferrer" : undefined}
                onClick={it.onClick}
                className="hover:text-foreground"
              >
                {it.label}
              </a>
            </li>
          ),
        )}
      </ul>
    </div>
  );
}

function Social({ href, icon, label }: { href: string; icon: React.ReactNode; label: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="inline-flex size-9 items-center justify-center rounded-full border border-white/10 bg-white/[0.03] text-muted-foreground transition hover:border-primary-glow/40 hover:text-foreground"
    >
      {icon}
    </a>
  );
}
